import { InventorySlotPackage } from '../../interfaces/InventoryDashboardInterfaces';

export enum TabKeys {
  GATEWAY = 'GATEWAY',
  STAR_PACKAGE = 'STAR_PACKAGE',
  MTHANKYOU = 'MTHANKYOU',
  WP_CLASS_1 = 'WP_CLASS_1',
  ILM_CLASS_1 = 'ILM_CLASS_1',
  MCONTEXTUAL_CLASS_1_CPM = 'MCONTEXTUAL_CLASS_1_CPM',
  DCONTEXTUAL_CLASS_1_CPM = 'DCONTEXTUAL_CLASS_1_CPM',
  PPP_CLASS_1 = 'PPP_CLASS_1',
  WD2_TARGETED_CLASS_1 = 'WD2_TARGETED_CLASS_1',
  ROS_DSP_UNGUARANTEED = 'ROS_DSP_UNGUARANTEED',
}

export interface TabConfig {
  key: TabKeys;
  label: string;
  tabNumber: number;
  segmentKeys: string[];
  tableHeaders: string[];
  totalValueKeys: string[];
  totalValueSources: Record<string, keyof InventorySlotPackage>;
}

// Consistent segment keys and table headers for all tabs
const COMMON_SEGMENT_KEYS = [
  'totalImpression',
  'overallValue',
  'percentageConsumed',
  'percentageAvailable',
  'cpm',
  'typeOfPurchase',
  'category',
];

const COMMON_TABLE_HEADERS = [
  'total-impressions',
  'overall-value',
  'percentage-consumed',
  'percentage-available',
  'cpm',
  'dashboard-table-type-of-purchase-header',
  'dashboard-table-category-header',
];

const COMMON_TOTAL_VALUE_KEYS = ['totalImpression', 'overallValue'];

const COMMON_TOTAL_VALUE_SOURCES: Record<string, keyof InventorySlotPackage> = {
  totalImpression: 'totalImpressionsCount',
  overallValue: 'totalOverallValue',
};

export const TAB_CONFIG: Record<TabKeys, TabConfig> = {
  [TabKeys.GATEWAY]: {
    key: TabKeys.GATEWAY,
    label: 'inventory-dashboard-gateway-tab-label',
    tabNumber: 0,
    segmentKeys: COMMON_SEGMENT_KEYS,
    tableHeaders: COMMON_TABLE_HEADERS,
    totalValueKeys: COMMON_TOTAL_VALUE_KEYS,
    totalValueSources: COMMON_TOTAL_VALUE_SOURCES,
  },
  [TabKeys.STAR_PACKAGE]: {
    key: TabKeys.STAR_PACKAGE,
    label: 'inventory-dashboard-star-package-tab-label',
    tabNumber: 1,
    segmentKeys: COMMON_SEGMENT_KEYS,
    tableHeaders: COMMON_TABLE_HEADERS,
    totalValueKeys: COMMON_TOTAL_VALUE_KEYS,
    totalValueSources: COMMON_TOTAL_VALUE_SOURCES,
  },
  [TabKeys.MTHANKYOU]: {
    key: TabKeys.MTHANKYOU,
    label: 'inventory-dashboard-mthankyou-tab-label',
    tabNumber: 2,
    segmentKeys: COMMON_SEGMENT_KEYS,
    tableHeaders: COMMON_TABLE_HEADERS,
    totalValueKeys: COMMON_TOTAL_VALUE_KEYS,
    totalValueSources: COMMON_TOTAL_VALUE_SOURCES,
  },
  [TabKeys.WP_CLASS_1]: {
    key: TabKeys.WP_CLASS_1,
    label: 'inventory-dashboard-wp-class-1-tab-label',
    tabNumber: 3,
    segmentKeys: COMMON_SEGMENT_KEYS,
    tableHeaders: COMMON_TABLE_HEADERS,
    totalValueKeys: COMMON_TOTAL_VALUE_KEYS,
    totalValueSources: COMMON_TOTAL_VALUE_SOURCES,
  },
  [TabKeys.ILM_CLASS_1]: {
    key: TabKeys.ILM_CLASS_1,
    label: 'inventory-dashboard-ilm-class-1-tab-label',
    tabNumber: 4,
    segmentKeys: COMMON_SEGMENT_KEYS,
    tableHeaders: COMMON_TABLE_HEADERS,
    totalValueKeys: COMMON_TOTAL_VALUE_KEYS,
    totalValueSources: COMMON_TOTAL_VALUE_SOURCES,
  },
  [TabKeys.MCONTEXTUAL_CLASS_1_CPM]: {
    key: TabKeys.MCONTEXTUAL_CLASS_1_CPM,
    label: 'inventory-dashboard-mcontextual-class-1-cpm-tab-label',
    tabNumber: 5,
    segmentKeys: COMMON_SEGMENT_KEYS,
    tableHeaders: COMMON_TABLE_HEADERS,
    totalValueKeys: COMMON_TOTAL_VALUE_KEYS,
    totalValueSources: COMMON_TOTAL_VALUE_SOURCES,
  },
  [TabKeys.DCONTEXTUAL_CLASS_1_CPM]: {
    key: TabKeys.DCONTEXTUAL_CLASS_1_CPM,
    label: 'inventory-dashboard-dcontextual-class-1-cpm-tab-label',
    tabNumber: 6,
    segmentKeys: COMMON_SEGMENT_KEYS,
    tableHeaders: COMMON_TABLE_HEADERS,
    totalValueKeys: COMMON_TOTAL_VALUE_KEYS,
    totalValueSources: COMMON_TOTAL_VALUE_SOURCES,
  },
  [TabKeys.PPP_CLASS_1]: {
    key: TabKeys.PPP_CLASS_1,
    label: 'inventory-dashboard-ppp-class-1-tab-label',
    tabNumber: 7,
    segmentKeys: COMMON_SEGMENT_KEYS,
    tableHeaders: COMMON_TABLE_HEADERS,
    totalValueKeys: COMMON_TOTAL_VALUE_KEYS,
    totalValueSources: COMMON_TOTAL_VALUE_SOURCES,
  },
  [TabKeys.WD2_TARGETED_CLASS_1]: {
    key: TabKeys.WD2_TARGETED_CLASS_1,
    label: 'inventory-dashboard-wd2-targeted-class-1-tab-label',
    tabNumber: 8,
    segmentKeys: COMMON_SEGMENT_KEYS,
    tableHeaders: COMMON_TABLE_HEADERS,
    totalValueKeys: COMMON_TOTAL_VALUE_KEYS,
    totalValueSources: COMMON_TOTAL_VALUE_SOURCES,
  },
  [TabKeys.ROS_DSP_UNGUARANTEED]: {
    key: TabKeys.ROS_DSP_UNGUARANTEED,
    label: 'inventory-dashboard-ros-dsp-unguaranteed-tab-label',
    tabNumber: 9,
    segmentKeys: COMMON_SEGMENT_KEYS,
    tableHeaders: COMMON_TABLE_HEADERS,
    totalValueKeys: COMMON_TOTAL_VALUE_KEYS,
    totalValueSources: COMMON_TOTAL_VALUE_SOURCES,
  },
};

// Export array for backward compatibility with existing tabbed navigation
export const tabLinks = Object.values(TAB_CONFIG);

// Helper function to get config by key
export const getTabConfig = (key: TabKeys): TabConfig | undefined => {
  return TAB_CONFIG[key];
};

// Helper function to get config by label (for current activeTab logic)
export const getTabConfigByLabel = (label: string): TabConfig | undefined => {
  return Object.values(TAB_CONFIG).find((config) => config.label === label);
};
